import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Megaphone, Play, Eye, Edit2, Trash2, Plus, Power, X } from 'lucide-react';
import { theme, cardStyle, inputStyle, labelStyle } from '../theme';

const DAYS = ['mon','tue','wed','thu','fri','sat','sun'];

export default function RecareCampaignsPage({ API_BASE }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    const r = await axios.get(`${API_BASE}/recare-campaigns`);
    setList(r.data || []);
  }, [API_BASE]);
  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    await axios.put(`${API_BASE}/recare-campaigns/${c._id}`, { enabled: !c.enabled });
    load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete campaign?')) return;
    await axios.delete(`${API_BASE}/recare-campaigns/${id}`); load();
  };

  const previewAud = async (c) => {
    const r = await axios.post(`${API_BASE}/recare-campaigns/${c._id}/preview`);
    setPreview({ campaign: c, ...r.data });
  };

  const runNow = async (c) => {
    if (!window.confirm(`Run "${c.name}" now? (will send WhatsApp to entire audience)`)) return;
    const r = await axios.post(`${API_BASE}/recare-campaigns/${c._id}/run`);
    alert(`✅ Sent: ${r.data.sent} | Failed: ${r.data.failed} | Total: ${r.data.total}`);
    load();
  };

  return (
    <div style={{ padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 18 }}>
        <h2 style={{ margin: 0, color: theme.navy }}>💌 AI Recare Campaigns</h2>
        <button onClick={()=>{ setEditing(null); setShowForm(true); }} style={btn(theme.primary)}><Plus size={14}/> New Campaign</button>
      </div>

      <div style={{ ...cardStyle, padding: 14, marginBottom: 16, background: theme.primaryLight }}>
        <div style={{ fontSize: 13, color: theme.navy, lineHeight: 1.6 }}>
          <strong>Kya hai?</strong> Auto-cron har 5 min check karta hai — jab campaign ka time + day match ho, AI un patients ko personalized WhatsApp bhejta hai jinhone X mahine se visit nahi ki.
          AI har message ko Hinglish me human-sounding banata hai, naam + last treatment include karta hai.
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 }}>
        {list.length === 0 && (
          <div style={{ ...cardStyle, padding: 40, textAlign: 'center', color: theme.textMuted, gridColumn: '1/-1' }}>
            <Megaphone size={36} style={{ opacity: 0.4 }}/>
            <div style={{ marginTop: 8 }}>Koi recare campaign nahi. Pehla banao!</div>
          </div>
        )}
        {list.map(c => (
          <div key={c._id} style={{ ...cardStyle, padding: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <h3 style={{ margin: 0, color: theme.navy, fontSize: 16 }}>{c.name}</h3>
                <div style={{ fontSize: 11, color: c.enabled ? theme.success : theme.textMuted, marginTop: 2, fontWeight: 700 }}>
                  {c.enabled ? '● ENABLED' : '○ DISABLED'} · {c.cronTime} · {(c.runDays||[]).join(', ').toUpperCase() || 'EVERY DAY'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={()=>toggle(c)} style={iconBtn} title="Toggle"><Power size={14} color={c.enabled ? theme.success : theme.textMuted}/></button>
                <button onClick={()=>{ setEditing(c); setShowForm(true); }} style={iconBtn}><Edit2 size={14}/></button>
                <button onClick={()=>remove(c._id)} style={{ ...iconBtn, color: theme.danger }}><Trash2 size={14}/></button>
              </div>
            </div>

            <div style={{ fontSize: 12, color: theme.textMuted, marginTop: 10, lineHeight: 1.6 }}>
              📅 Audience: not seen in {c.monthsSinceLastVisit}+ months<br/>
              📡 Channel: {c.channel}{c.aiPersonalize && ' · AI personalized'}<br/>
              📊 Sent total: {c.totalSent} {c.lastRunAt && `· Last run: ${new Date(c.lastRunAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`}
            </div>

            <div style={{ marginTop: 12, padding: 10, background: '#f7f9fc', borderRadius: theme.radiusSm, fontSize: 12, fontFamily: 'Courier', color: theme.navy }}>
              {c.messageTemplate.slice(0, 160)}{c.messageTemplate.length > 160 ? '…' : ''}
            </div>

            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button onClick={()=>previewAud(c)} style={{ ...btn(theme.secondary), flex: 1 }}><Eye size={13}/> Preview</button>
              <button onClick={()=>runNow(c)} style={{ ...btn(theme.primary), flex: 1 }}><Play size={13}/> Run Now</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && <CampaignForm API_BASE={API_BASE} initial={editing} onClose={()=>{ setShowForm(false); setEditing(null); }} onSaved={()=>{ setShowForm(false); setEditing(null); load(); }}/>}
      {preview && <PreviewModal preview={preview} onClose={()=>setPreview(null)}/>}
    </div>
  );
}

function CampaignForm({ API_BASE, initial, onClose, onSaved }) {
  const [f, setF] = useState(initial || {
    name: '', enabled: true, monthsSinceLastVisit: 6, channel: 'whatsapp',
    aiPersonalize: true, cronTime: '10:00', runDays: ['mon','wed','fri'],
    messageTemplate: 'Hi {{name}}, namaste! Aapki last visit {{lastVisit}} ko thi ({{lastTreatment}}). Free check-up ke liye slot available hai — reply YES to book.',
  });
  const [busy, setBusy] = useState(false);

  const toggleDay = (d) => {
    const days = f.runDays || [];
    setF({ ...f, runDays: days.includes(d) ? days.filter(x=>x!==d) : [...days, d] });
  };

  const save = async () => {
    setBusy(true);
    try {
      if (initial) await axios.put(`${API_BASE}/recare-campaigns/${initial._id}`, f);
      else         await axios.post(`${API_BASE}/recare-campaigns`, f);
      onSaved();
    } catch (e) { alert(e.response?.data?.error || e.message); }
    setBusy(false);
  };

  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(580px, 92vw)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>{initial ? 'Edit Campaign' : 'New Recare Campaign'}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ marginTop: 14 }}>
          <label style={labelStyle}>Campaign Name</label>
          <input value={f.name} onChange={e=>setF({...f, name:e.target.value})} style={inputStyle} placeholder="6-month recall — sleep apnea patients"/>

          <label style={labelStyle}>Months since last visit</label>
          <input type="number" value={f.monthsSinceLastVisit} onChange={e=>setF({...f, monthsSinceLastVisit:Number(e.target.value)})} style={inputStyle}/>

          <label style={labelStyle}>Channel</label>
          <select value={f.channel} onChange={e=>setF({...f, channel:e.target.value})} style={inputStyle}>
            <option value="whatsapp">WhatsApp</option><option value="sms">SMS</option><option value="both">Both</option>
          </select>

          <label style={labelStyle}>Run Time (HH:MM, server local)</label>
          <input value={f.cronTime} onChange={e=>setF({...f, cronTime:e.target.value})} style={inputStyle} placeholder="10:00"/>

          <label style={labelStyle}>Run Days</label>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {DAYS.map(d => (
              <button key={d} type="button" onClick={()=>toggleDay(d)} style={{
                padding: '6px 12px', borderRadius: theme.radiusSm, fontSize: 11, cursor: 'pointer', fontWeight: 700,
                border: `1px solid ${(f.runDays||[]).includes(d) ? theme.primary : theme.border}`,
                background: (f.runDays||[]).includes(d) ? theme.primary : '#fff',
                color: (f.runDays||[]).includes(d) ? '#fff' : theme.navy,
              }}>{d.toUpperCase()}</button>
            ))}
          </div>

          <label style={labelStyle}>Message Template ({'{{name}}, {{lastVisit}}, {{lastTreatment}}'} supported)</label>
          <textarea rows={4} value={f.messageTemplate} onChange={e=>setF({...f, messageTemplate:e.target.value})} style={{ ...inputStyle, resize: 'vertical' }}/>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={f.aiPersonalize} onChange={e=>setF({...f, aiPersonalize:e.target.checked})}/>
            🤖 AI personalize each message (Gemini)
          </label>

          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginTop: 6 }}>
            <input type="checkbox" checked={f.enabled} onChange={e=>setF({...f, enabled:e.target.checked})}/>
            Enabled (cron will pick this up)
          </label>
        </div>
        <button onClick={save} disabled={busy} style={{ ...btn(theme.primary), marginTop: 14, width: '100%' }}>{busy ? 'Saving...' : 'Save Campaign'}</button>
      </div>
    </div>
  );
}

function PreviewModal({ preview, onClose }) {
  return (
    <div style={overlay}>
      <div style={{ ...cardStyle, padding: 24, width: 'min(560px, 92vw)', maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <h3 style={{ margin: 0 }}>📋 Audience Preview — {preview.campaign.name}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer' }}><X/></button>
        </div>
        <div style={{ fontSize: 14, color: theme.navy, marginTop: 8 }}>
          Total patients matching: <strong>{preview.count}</strong>
        </div>
        <div style={{ marginTop: 14, maxHeight: 400, overflowY: 'auto' }}>
          {preview.sample.map(p => (
            <div key={p._id} style={{ padding: 10, borderBottom: `1px solid ${theme.borderLight}`, fontSize: 13 }}>
              <strong>{p.name}</strong> · <span style={{ color: theme.textMuted }}>📱 {p.phone}</span>
              <div style={{ fontSize: 11, color: theme.textMuted }}>Last visit: {new Date(p.lastVisit).toLocaleDateString('en-IN')} {p.lastTreatment && `· ${p.lastTreatment}`}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const btn = (c) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '8px 14px', background: c, color: '#fff', border: 'none', borderRadius: theme.radiusSm, cursor: 'pointer', fontWeight: 600, fontSize: 13 });
const iconBtn = { background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, color: theme.navy };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
